import { useCallback, useRef, useState } from 'react'
import { Platform, useWindowDimensions, type View } from 'react-native'

const DEFAULT_GAP_BELOW = 6
const DEFAULT_EDGE_PADDING = 16

interface UseDropdownOverlayPositionOptions {
  overlayWidth: number
  /** 'left': overlay left edge aligns with trigger. 'right': overlay right edge aligns with trigger (right-overflow protected). */
  alignment?: 'left' | 'right'
  gapBelow?: number
  edgePadding?: number
}

interface WebOverlayStyle {
  position: 'fixed'
  left: number
  top: number
  marginTop: 0
}

interface UseDropdownOverlayPositionResult {
  triggerRef: React.RefObject<View | null>
  /** Native only: margin offset to prevent overlay from overflowing the screen edge. */
  overlayOffset: { marginLeft: number } | null
  /** Web only: fixed-position style for the dropdown container. Cast to containerStyle when passing to Dropdown. */
  webOverlayStyle: WebOverlayStyle | undefined
  /** Call this in the Dropdown's onFocus handler. */
  measure: () => void
}

export function useDropdownOverlayPosition({
  overlayWidth,
  alignment = 'left',
  gapBelow = DEFAULT_GAP_BELOW,
  edgePadding = DEFAULT_EDGE_PADDING,
}: UseDropdownOverlayPositionOptions): UseDropdownOverlayPositionResult {
  const { width: screenWidth } = useWindowDimensions()
  const triggerRef = useRef<View | null>(null)
  const [overlayOffset, setOverlayOffset] = useState<{ marginLeft: number } | null>(null)
  const [webOverlayPosition, setWebOverlayPosition] = useState<{ left: number; top: number } | null>(null)

  const measure = useCallback(() => {
    triggerRef.current?.measureInWindow((triggerX, triggerY, triggerWidth, triggerHeight) => {
      if (Platform.OS === 'web') {
        let left: number
        if (alignment === 'right') {
          left = Math.min(
            triggerX + (triggerWidth ?? 0) - overlayWidth,
            screenWidth - edgePadding - overlayWidth,
          )
          left = Math.max(edgePadding, left)
        } else {
          left = triggerX
        }
        setWebOverlayPosition({ left, top: triggerY + (triggerHeight ?? 0) + gapBelow })
        setOverlayOffset(null)
      } else {
        let marginLeft = 0
        if (alignment === 'right') {
          const overlayRight = triggerX + overlayWidth
          const maxRight = screenWidth - edgePadding
          marginLeft = overlayRight > maxRight ? maxRight - overlayWidth - triggerX : 0
        } else {
          marginLeft = triggerX < edgePadding ? edgePadding - triggerX : 0
        }
        setOverlayOffset(marginLeft !== 0 ? { marginLeft } : null)
        setWebOverlayPosition(null)
      }
    })
  }, [screenWidth, overlayWidth, alignment, gapBelow, edgePadding])

  const webOverlayStyle: WebOverlayStyle | undefined =
    Platform.OS === 'web' && webOverlayPosition
      ? { position: 'fixed', left: webOverlayPosition.left, top: webOverlayPosition.top, marginTop: 0 }
      : undefined

  return { triggerRef, overlayOffset, webOverlayStyle, measure }
}
